/**
 * /add-Batching (client.md §10). Beide Limits einhalten: Payload < 4 MB und höchstens
 * 8 Dokumente pro Request. Batches sequenziell senden, Teilfehler einsammeln.
 */
import { add, type ApiCtx } from "../api/client";
import type { AddDocument, AddImage, AddWrittenErr, AddWrittenOk } from "../api/types";

const MAX_BYTES = 4 * 1024 * 1024;
const MAX_DOCS = 8;

export type ChunkProgress = { batch: number; totalBatches: number; count: number };
export type ChunkError = { doc_id: string; error: string };

function byteSize(item: AddDocument | AddImage): number {
  return "content" in item ? item.content.length : item.content_base64.length;
}

export function batchItems<T extends AddDocument | AddImage>(items: T[]): T[][] {
  const batches: T[][] = [];
  let current: T[] = [];
  let currentBytes = 0;
  for (const item of items) {
    const bytes = byteSize(item);
    if (current.length >= MAX_DOCS || (current.length > 0 && currentBytes + bytes > MAX_BYTES)) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(item);
    currentBytes += bytes;
  }
  if (current.length) batches.push(current);
  return batches;
}

function isErr(w: AddWrittenOk | AddWrittenErr): w is AddWrittenErr {
  return "error" in w;
}

export async function uploadInBatches(
  ctx: ApiCtx,
  documents: AddDocument[],
  images: AddImage[] = [],
  onProgress?: (p: ChunkProgress) => void,
): Promise<{ errors: ChunkError[] }> {
  const docBatches = batchItems(documents);
  const imgBatches = batchItems(images);
  const totalBatches = docBatches.length + imgBatches.length;
  const errors: ChunkError[] = [];
  let batchNo = 0;

  for (const batch of docBatches) {
    batchNo++;
    onProgress?.({ batch: batchNo, totalBatches, count: batch.length });
    const res = await add(ctx, batch, []);
    // Bei code<0 (z. B. "workspace does not exist yet") fehlt `written` komplett —
    // ohne diesen Zweig blieb `errors` leer und der Fehlschlag war für die
    // Nutzer:in unsichtbar (kein Toast, keine Dokumente im Workspace).
    if (res.code < 0) {
      console.error("[chunker] add batch failed", res.msg);
      for (const item of batch) errors.push({ doc_id: item.doc_id, error: res.msg ?? "add fehlgeschlagen" });
      continue;
    }
    for (const w of res.written ?? []) if (isErr(w)) errors.push(w);
  }
  for (const batch of imgBatches) {
    batchNo++;
    onProgress?.({ batch: batchNo, totalBatches, count: batch.length });
    const res = await add(ctx, [], batch);
    if (res.code < 0) {
      console.error("[chunker] add batch (images) failed", res.msg);
      for (const item of batch) errors.push({ doc_id: item.doc_id, error: res.msg ?? "add fehlgeschlagen" });
      continue;
    }
    for (const w of res.written ?? []) if (isErr(w)) errors.push(w);
  }
  return { errors };
}
